export interface MaltaOfferedCourseEntry {
  label: string;
  category: string;
  tuitionFee: string;
  admissionSemester: string;
  yearOfStudy: string;
  duration: string;
  medicalInsurance: string;
  transfer: string;
}

type SemesterRow = Pick<
  MaltaOfferedCourseEntry,
  "admissionSemester" | "yearOfStudy" | "duration" | "medicalInsurance" | "transfer"
> & {
  labelSuffix: string;
};

const buildCourseGroup = (
  baseName: string,
  category: string,
  tuitionFee: string,
  rows: SemesterRow[],
): MaltaOfferedCourseEntry[] =>
  rows.map(({ labelSuffix, admissionSemester, yearOfStudy, duration, medicalInsurance, transfer }) => ({
    label: `${baseName} ${labelSuffix}`.trim(),
    category,
    tuitionFee,
    admissionSemester,
    yearOfStudy,
    duration,
    medicalInsurance,
    transfer,
  }));

const MEDICINE_LIKE_ROWS: SemesterRow[] = [
  {
    labelSuffix: "1st year 1st semester",
    admissionSemester: "1st semester",
    yearOfStudy: "",
    duration: "6 years",
    medicalInsurance: "600",
    transfer: "",
  },
  {
    labelSuffix: "1st year 2nd semester",
    admissionSemester: "2nd semester",
    yearOfStudy: "1st year 2nd semester",
    duration: "5,5 years",
    medicalInsurance: "600",
    transfer: "3 000",
  },
  {
    labelSuffix: "2nd year",
    admissionSemester: "3rd semester",
    yearOfStudy: "2nd year",
    duration: "5 years",
    medicalInsurance: "500",
    transfer: "3 000",
  },
  {
    labelSuffix: "2nd year 2nd semester",
    admissionSemester: "4th semester",
    yearOfStudy: "2nd year 2nd semester",
    duration: "4,5 years",
    medicalInsurance: "500",
    transfer: "3 000",
  },
  {
    labelSuffix: "3rd year",
    admissionSemester: "5th semester",
    yearOfStudy: "3rd year",
    duration: "4 years",
    medicalInsurance: "400",
    transfer: "3 000",
  },
  {
    labelSuffix: "3rd year 2nd semester",
    admissionSemester: "6th semester",
    yearOfStudy: "3rd year 2nd semester",
    duration: "3,5 years",
    medicalInsurance: "400",
    transfer: "3 000",
  },
  {
    labelSuffix: "4th year",
    admissionSemester: "7th semester",
    yearOfStudy: "4th year",
    duration: "3 years",
    medicalInsurance: "300",
    transfer: "4 000",
  },
  {
    labelSuffix: "4th year 2nd semester",
    admissionSemester: "8th semester",
    yearOfStudy: "4th year 2nd semester",
    duration: "2,5 years",
    medicalInsurance: "300",
    transfer: "4 000",
  },
  {
    labelSuffix: "5th year",
    admissionSemester: "9th semester",
    yearOfStudy: "5th year",
    duration: "2 years",
    medicalInsurance: "200",
    transfer: "5 000",
  },
  {
    labelSuffix: "5th year 2nd semester",
    admissionSemester: "10th semester",
    yearOfStudy: "5th year 2nd semester",
    duration: "1,5 years",
    medicalInsurance: "200",
    transfer: "5 000",
  },
];

const DENTISTRY_LIKE_ROWS: SemesterRow[] = [
  {
    labelSuffix: "1st year 1st semester",
    admissionSemester: "1st semester",
    yearOfStudy: "",
    duration: "5 years",
    medicalInsurance: "500",
    transfer: "",
  },
  {
    labelSuffix: "1st year 2nd semester",
    admissionSemester: "2nd semester",
    yearOfStudy: "1st year 2nd semester",
    duration: "4,5 years",
    medicalInsurance: "500",
    transfer: "3 000",
  },
  {
    labelSuffix: "2nd year",
    admissionSemester: "3rd semester",
    yearOfStudy: "2nd year",
    duration: "4 years",
    medicalInsurance: "400",
    transfer: "3 000",
  },
  {
    labelSuffix: "2nd year 2nd semester",
    admissionSemester: "4th semester",
    yearOfStudy: "2nd year 2nd semester",
    duration: "3,5 years",
    medicalInsurance: "400",
    transfer: "3 000",
  },
  {
    labelSuffix: "3rd year",
    admissionSemester: "5th semester",
    yearOfStudy: "3rd year",
    duration: "3 years",
    medicalInsurance: "300",
    transfer: "3 000",
  },
  {
    labelSuffix: "3rd year 2nd semester",
    admissionSemester: "6th semester",
    yearOfStudy: "3rd year 2nd semester",
    duration: "2,5 years",
    medicalInsurance: "300",
    transfer: "3 000",
  },
  {
    labelSuffix: "4th year",
    admissionSemester: "7th semester",
    yearOfStudy: "4th year",
    duration: "2 years",
    medicalInsurance: "200",
    transfer: "4 000",
  },
  {
    labelSuffix: "4th year 2nd semester",
    admissionSemester: "8th semester",
    yearOfStudy: "4th year 2nd semester",
    duration: "1,5 years",
    medicalInsurance: "200",
    transfer: "4 000",
  },
];

const NURSING_LIKE_ROWS: SemesterRow[] = [
  {
    labelSuffix: "1st year 1st semester",
    admissionSemester: "1st semester",
    yearOfStudy: "",
    duration: "4 years",
    medicalInsurance: "400",
    transfer: "",
  },
  {
    labelSuffix: "1st year 2nd semester",
    admissionSemester: "2nd semester",
    yearOfStudy: "1st year 2nd semester",
    duration: "3,5 years",
    medicalInsurance: "400",
    transfer: "3 000",
  },
  {
    labelSuffix: "2nd year",
    admissionSemester: "3rd semester",
    yearOfStudy: "2nd year",
    duration: "3 years",
    medicalInsurance: "300",
    transfer: "3 000",
  },
  {
    labelSuffix: "2nd year 2nd semester",
    admissionSemester: "4th semester",
    yearOfStudy: "2nd year 2nd semester",
    duration: "2,5 years",
    medicalInsurance: "300",
    transfer: "3 000",
  },
  {
    labelSuffix: "3rd year",
    admissionSemester: "5th semester",
    yearOfStudy: "3rd year",
    duration: "2 years",
    medicalInsurance: "200",
    transfer: "3 000",
  },
  {
    labelSuffix: "3rd year 2nd semester",
    admissionSemester: "6th semester",
    yearOfStudy: "3rd year 2nd semester",
    duration: "1,5 years",
    medicalInsurance: "200",
    transfer: "3 000",
  },
];

const BACHELOR_LIKE_ROWS: SemesterRow[] = [
  {
    labelSuffix: "1st year 1st semester",
    admissionSemester: "1st semester",
    yearOfStudy: "",
    duration: "4 years",
    medicalInsurance: "400",
    transfer: "",
  },
  {
    labelSuffix: "1st year 2nd semester",
    admissionSemester: "2nd semester",
    yearOfStudy: "1st year 2nd semester",
    duration: "3,5 years",
    medicalInsurance: "400",
    transfer: "1 500",
  },
  {
    labelSuffix: "2nd year",
    admissionSemester: "3rd semester",
    yearOfStudy: "2nd year",
    duration: "3 years",
    medicalInsurance: "300",
    transfer: "1 500",
  },
  {
    labelSuffix: "2nd year 2nd semester",
    admissionSemester: "4th semester",
    yearOfStudy: "2nd year 2nd semester",
    duration: "2,5 years",
    medicalInsurance: "300",
    transfer: "1 500",
  },
  {
    labelSuffix: "3rd year",
    admissionSemester: "5th semester",
    yearOfStudy: "3rd year",
    duration: "2 years",
    medicalInsurance: "200",
    transfer: "1 500",
  },
  {
    labelSuffix: "3rd year 2nd semester",
    admissionSemester: "6th semester",
    yearOfStudy: "3rd year 2nd semester",
    duration: "1,5 years",
    medicalInsurance: "200",
    transfer: "1 500",
  },
];

export const MALTA_OFFERED_COURSES: MaltaOfferedCourseEntry[] = [
  ...buildCourseGroup("Preparatory", "Preparatory", "1 500", [
    {
      labelSuffix: "1st semester",
      admissionSemester: "1st semester",
      yearOfStudy: "",
      duration: "1 year",
      medicalInsurance: "100",
      transfer: "",
    },
  ]),
  ...buildCourseGroup("Medicine", "Medicine", "5 000", MEDICINE_LIKE_ROWS),
  ...buildCourseGroup("Pediatrics", "Pediatrics", "5 000", MEDICINE_LIKE_ROWS),
  ...buildCourseGroup("Dentistry", "Dentistry", "5 000", DENTISTRY_LIKE_ROWS),
  ...buildCourseGroup("Pharmacy", "Pharmacy", "5 000", DENTISTRY_LIKE_ROWS),
  ...buildCourseGroup("Medical Laboratory Science", "Medical Laboratory Science", "5 000", DENTISTRY_LIKE_ROWS),
  ...buildCourseGroup("Nursing", "Nursing", "5 000", NURSING_LIKE_ROWS),
  ...buildCourseGroup("Bachelor non-med", "Bachelor non-med", "2 000", BACHELOR_LIKE_ROWS),
  ...buildCourseGroup("Bachelor med", "Bachelor med", "5 000", BACHELOR_LIKE_ROWS.map((row) => ({
    ...row,
    transfer: row.transfer ? "3 000" : "",
  }))),
  ...buildCourseGroup("Master non-med", "Master non-med", "2 000", [
    {
      labelSuffix: "1st semester",
      admissionSemester: "1st semester",
      yearOfStudy: "",
      duration: "1.5 years",
      medicalInsurance: "200",
      transfer: "",
    },
  ]),
  ...buildCourseGroup("Master med", "Master med", "5 000", [
    {
      labelSuffix: "1st semester",
      admissionSemester: "1st semester",
      yearOfStudy: "",
      duration: "1.5 years",
      medicalInsurance: "200",
      transfer: "",
    },
  ]),
  ...buildCourseGroup("PG med", "PG med", "6 500", [
    {
      labelSuffix: "1st semester",
      admissionSemester: "1st semester",
      yearOfStudy: "",
      duration: "2 years",
      medicalInsurance: "200",
      transfer: "",
    },
  ]),
  ...buildCourseGroup("Internship in Family Medicine", "Internship in Family Medicine", "5 000", [
    {
      labelSuffix: "1st semester",
      admissionSemester: "1st semester",
      yearOfStudy: "",
      duration: "1 year",
      medicalInsurance: "100",
      transfer: "",
    },
  ]),
];

export const MALTA_OFFERED_COURSES_BY_LABEL: Record<string, MaltaOfferedCourseEntry> = Object.fromEntries(
  MALTA_OFFERED_COURSES.map((entry) => [entry.label, entry]),
);

export const MALTA_OFFERED_COURSE_LABELS = MALTA_OFFERED_COURSES.map((entry) => entry.label);
